/**
 * The unit create/edit form (Units redesign, Slice 2).
 *
 * PURE MOVE. This is the ~920-line block that lived inline in
 * `AdminApartmentsPage.js` (lines 478-1397 before the extraction), carried over
 * verbatim. Nothing about what it renders, validates, submits or styles was
 * touched — not a label, not a class, not a field order. The page keeps owning
 * the data flow; this file owns nothing but the markup.
 *
 * WHY THE INDENTATION LOOKS DEEP. The body below still carries the indentation
 * it had inside the page's JSX tree, so the moved lines are byte-identical to
 * the ones that were deleted and the move can be PROVEN rather than eyeballed:
 *
 *   diff <(git show HEAD:...AdminApartmentsPage.js | sed -n '478,1397p') \
 *        <(sed -n '<body-range>p' UnitFormModal.js)
 *
 * Re-indenting would have rewritten all 920 lines and thrown that proof away
 * for cosmetics. A later whitespace-only commit can normalise it; doing it here
 * would have meant reviewing a 920-line diff for a move that is supposed to be
 * reviewable at a glance. JSX text nodes are also whitespace-sensitive enough in
 * this repo (see CLAUDE.md §8.4 on the visual-edits Babel plugin shifting text
 * node layout) that a blanket dedent is not a free operation.
 *
 * PROPS. Three, and no more: the page's `modal` object (the full return value of
 * `useUnitFormModal`, which the body already addressed as `modal.*` on 144
 * lines), plus the two page collections the body reads directly — `units` for
 * the edit-mode unit id and `properties` for the property select.
 *
 * `modal` is passed whole ON PURPOSE. It is not a grab-bag: it is one named
 * abstraction the hook already defines, and 45 of its fields are read here.
 * Destructuring it into 45 props would have rewritten every one of those 144
 * lines, which is the opposite of a pure move — and each rewritten line is a
 * chance to drop a field silently.
 */
import React, { Fragment } from "react";

import { verifyAdminAddress } from "../../../api/adminData";
import { SWISS_CANTON_CODES } from "../../../constants/swissCantons";
import { buildGoogleMapsSearchUrl } from "../../../utils/googleMapsUrl";
import { formatLandlordSummaryLine as landlordSelectLabel } from "../../../utils/landlordDisplayFormat";
import {
  propertyManagerSelectLabel,
  computeLeaseStatusKey,
  leaseStatusLabel,
  formatCurrencyChf2,
  formatCurrency,
} from "./apartmentsHelpers";

const MODAL_COST_TYPE_OPTIONS = [
  "Miete",
  "Nebenkosten",
  "Reinigung",
  "Internet",
  "Sonstiges",
];

export default function UnitFormModal({ modal, units, properties }) {
  if (!modal.isModalOpen) return null;

  return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[14px] border border-black/10 dark:border-white/[0.07] bg-white dark:bg-[#141824] p-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h3 className="text-[18px] font-bold text-[#0f172a] dark:text-[#eef2ff]">
                  {modal.editingId ? "Unit bearbeiten" : "Neue Unit hinzufügen"}
                </h3>
                <p className="mt-1 text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
                  {modal.editingId
                    ? "Bearbeite hier die vorhandene Unit."
                    : "Die Unit ID wird automatisch vergeben."}
                </p>
              </div>

              <button
                type="button"
                onClick={modal.handleCloseModal}
                className="text-2xl leading-none text-[#64748b] dark:text-[#6b7a9a] hover:text-[#0f172a] dark:hover:text-[#eef2ff]"
              >
                ×
              </button>
            </div>

            <div className="mb-6 rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4">
              <p className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                {modal.editingId ? "Unit ID" : "Automatische Unit ID"}
              </p>
              <p className="mt-1 text-[16px] font-bold text-[#0f172a] dark:text-[#eef2ff]">
                {modal.editingId
                  ? units.find((item) => item.id === modal.editingId)?.unitId
                  : modal.nextUnitId}
              </p>
            </div>

            {modal.saveError && (
              <p className="mb-4 text-[13px] text-[#f87171]">{modal.saveError}</p>
            )}
            <form onSubmit={modal.handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Ort
                  </label>
                  <input
                    type="text"
                    name="place"
                    value={modal.formData.place}
                    onChange={modal.handleChange}
                    required
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    PLZ
                  </label>
                  <input
                    type="text"
                    name="zip"
                    value={modal.formData.zip}
                    onChange={modal.handleUnitPostalCodeChange}
                    required
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  />
                  {modal.unitPlzNotFound ? (
                    <p className="mt-1 text-xs text-[#64748b] dark:text-[#6b7a9a]">PLZ nicht gefunden</p>
                  ) : null}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Adresse
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={modal.formData.address}
                    onChange={modal.handleChange}
                    required
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor="unit-city-id"
                    className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]"
                  >
                    Stadt (optional)
                  </label>
                  <select
                    id="unit-city-id"
                    name="city_id"
                    value={modal.formData.city_id}
                    onChange={modal.handleChange}
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  >
                    <option value="">— Keine Stadt zugeordnet</option>
                    {modal.formData.city_id &&
                    !modal.cities.some((c) => c.id === modal.formData.city_id) ? (
                      <option value={modal.formData.city_id}>
                        {modal.citiesStatus === "loading"
                          ? "Städte werden geladen …"
                          : "Gespeicherte Stadt (nicht in der Liste)"}
                      </option>
                    ) : null}
                    {modal.cities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name_de}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-[#64748b] dark:text-[#6b7a9a]">
                    {modal.citiesStatus === "error"
                      ? "Städte konnten nicht geladen werden. Eine gespeicherte Stadt bleibt erhalten."
                      : "Liefert das Land der Objektadresse – auch ohne Liegenschaft."}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    EGID / aWN (optional)
                  </label>
                  <input
                    type="text"
                    name="egid_awn"
                    value={modal.formData.egid_awn}
                    onChange={modal.handleChange}
                    placeholder="z.B. EGID 123456789 / aWN 2.OG rechts"
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  />
                </div>

                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-2">
                  <p className={modal.unitGeoLine.className}>{modal.unitGeoLine.text}</p>
                  {modal.editingId ? (
                    <button
                      type="button"
                      onClick={modal.handleRetryUnitGeocode}
                      disabled={modal.saving || modal.unitGeocodingRetrying}
                      className="shrink-0 rounded-[8px] border border-black/10 dark:border-white/[0.1] bg-transparent px-3 py-2 text-xs font-semibold text-[#64748b] hover:bg-slate-100 dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {modal.unitGeocodingRetrying ? "Berechne …" : "Koordinaten erneut berechnen"}
                    </button>
                  ) : null}
                </div>

                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                  <div className="md:col-span-1">
                    <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Kanton (optional)
                    </label>
                    <select
                      name="canton"
                      value={modal.formData.canton || ""}
                      onChange={(e) =>
                        modal.setFormData((f) => ({ ...f, canton: e.target.value }))
                      }
                      disabled={modal.saving || modal.unitCantonLockedByPlz}
                      className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none disabled:opacity-50"
                    >
                      <option value="">—</option>
                      {modal.formData.canton && !SWISS_CANTON_CODES.includes(modal.formData.canton) ? (
                        <option value={modal.formData.canton}>{modal.formData.canton}</option>
                      ) : null}
                      {SWISS_CANTON_CODES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2 flex flex-col gap-1.5 pt-7">
                    <button
                      type="button"
                      onClick={() => {
                        window.open(
                          buildGoogleMapsSearchUrl(
                            modal.formData.address,
                            modal.formData.zip,
                            modal.formData.place
                          ),
                          "_blank",
                          "noopener,noreferrer"
                        );
                        modal.setUnitAddrBusy(true);
                        modal.setUnitCantonHint("Kanton wird ermittelt …");
                        verifyAdminAddress({
                          address_line1: modal.formData.address,
                          postal_code: modal.formData.zip,
                          city: modal.formData.place,
                        })
                          .then((res) => {
                            const c = res?.normalized?.canton;
                            if (res?.valid && c != null && String(c).trim() !== "") {
                              const code = String(c).trim().toUpperCase();
                              modal.setFormData((f) => ({ ...f, canton: code }));
                              modal.setUnitCantonHint("Kanton automatisch erkannt.");
                            } else {
                              modal.setUnitCantonHint(
                                "Kein Kanton automatisch ermittelbar. Bitte bei Bedarf manuell wählen."
                              );
                            }
                          })
                          .catch(() =>
                            modal.setUnitCantonHint("Kanton konnte nicht automatisch ermittelt werden.")
                          )
                          .finally(() => modal.setUnitAddrBusy(false));
                      }}
                      disabled={
                        modal.saving ||
                        modal.unitAddrBusy ||
                        !(String(modal.formData.address || "").trim()) ||
                        !(String(modal.formData.zip || "").trim()) ||
                        !(String(modal.formData.place || "").trim())
                      }
                      className="self-start rounded-[8px] border border-black/10 dark:border-white/[0.1] bg-transparent px-3 py-2 text-xs font-semibold text-[#64748b] hover:bg-slate-100 dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {modal.unitAddrBusy ? "…" : "Adresse prüfen"}
                    </button>
                    <p className="text-xs text-[#64748b] dark:text-[#6b7a9a]">
                      Öffnet Google Maps in einem neuen Tab. Der Kanton wird im Hintergrund ergänzt, wenn die
                      Abfrage einen Wert liefert.
                    </p>
                    {modal.unitCantonHint ? (
                      <p className="text-xs text-[#64748b] dark:text-[#6b7a9a]">{modal.unitCantonHint}</p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Typ
                  </label>
                  <select
                    name="type"
                    value={modal.formData.type}
                    onChange={modal.handleChange}
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  >
                    <option value="Apartment">Apartment</option>
                    <option value="Co-Living">Co-Living</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Liegenschaft (optional)
                  </label>
                  <select
                    name="property_id"
                    value={modal.formData.property_id}
                    onChange={modal.handleChange}
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  >
                    <option value="">— Nicht zugewiesen</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title || p.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Verwaltung (optional)
                    </label>
                    <button
                      type="button"
                      title="Neue Verwaltung anlegen"
                      onClick={() => modal.setCreateLandlordModalOpen(true)}
                      className="shrink-0 rounded-md border border-black/10 px-2 py-0.5 text-[12px] font-semibold leading-none text-[#2563eb] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#5b8cff] dark:hover:bg-white/[0.06]"
                    >
                      +
                    </button>
                  </div>
                  <input
                    type="search"
                    value={modal.landlordFilter}
                    onChange={(e) => modal.setLandlordFilter(e.target.value)}
                    placeholder="Suchen…"
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-2 mb-2 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none"
                    autoComplete="off"
                  />
                  <select
                    name="landlord_id"
                    value={modal.formData.landlord_id}
                    onChange={modal.handleChange}
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  >
                    <option value="">— Keine Auswahl</option>
                    {modal.filteredLandlordsForSelect.map((l) => (
                      <option key={l.id} value={l.id}>
                        {landlordSelectLabel(l)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Bewirtschafter (optional)
                    </label>
                    <button
                      type="button"
                      title="Neuen Bewirtschafter anlegen"
                      onClick={() => modal.setCreatePmModalOpen(true)}
                      className="shrink-0 rounded-md border border-black/10 px-2 py-0.5 text-[12px] font-semibold leading-none text-[#2563eb] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#5b8cff] dark:hover:bg-white/[0.06]"
                    >
                      +
                    </button>
                  </div>
                  <input
                    type="search"
                    value={modal.propertyManagerFilter}
                    onChange={(e) => modal.setPropertyManagerFilter(e.target.value)}
                    placeholder="Suchen…"
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-2 mb-2 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none"
                    autoComplete="off"
                  />
                  <select
                    name="property_manager_id"
                    value={modal.formData.property_manager_id}
                    onChange={modal.handleChange}
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  >
                    <option value="">— Keine Auswahl</option>
                    {modal.filteredPropertyManagersForSelect.map((pm) => (
                      <option key={pm.id} value={pm.id}>
                        {propertyManagerSelectLabel(pm)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Zimmer gesamt
                  </label>
                  {modal.isCoLivingType ? (
                    <p className="mb-2 text-xs text-[#64748b] dark:text-[#6b7a9a]">
                      Zimmer-Details erscheinen, sobald «Zimmer gesamt» größer als 0 ist.
                    </p>
                  ) : null}
                  <input
                    type="number"
                    name="rooms"
                    value={modal.formData.rooms}
                    onChange={modal.handleChange}
                    required
                    placeholder="z. B. 3"
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  />
                </div>

                {modal.isCoLivingType &&
                  !modal.editingId &&
                  modal.parsedRoomsTotal > 0 &&
                  modal.coLivingRowsForDisplay.map((row, idx) => (
                    <div
                      key={idx}
                      className="md:col-span-2 rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4"
                    >
                      <p className="mb-3 text-[13px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                        Zimmer {idx + 1}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div>
                          <label className="mb-1 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">Name</label>
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) =>
                              modal.handleCoLivingRoomChange(idx, "name", e.target.value)
                            }
                            required
                            className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-3 py-2 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                            Geplanter Zimmerpreis (CHF)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={row.price}
                            onChange={(e) =>
                              modal.handleCoLivingRoomChange(idx, "price", e.target.value)
                            }
                            required
                            className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-3 py-2 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none"
                          />
                          <p className="mt-1 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                            Wird für Prognosen und Vollbelegung verwendet
                          </p>
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">Etage</label>
                          <input
                            type="number"
                            min="0"
                            value={row.floor}
                            onChange={(e) =>
                              modal.handleCoLivingRoomChange(idx, "floor", e.target.value)
                            }
                            className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-3 py-2 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">Fläche (m²)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.size_m2}
                            onChange={(e) =>
                              modal.handleCoLivingRoomChange(idx, "size_m2", e.target.value)
                            }
                            className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-3 py-2 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">Status</label>
                          <div className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-3 py-2 text-sm text-[#0f172a] dark:text-[#eef2ff]">
                            <span className="font-medium">Frei</span>
                            <span className="mt-0.5 block text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                              Wird automatisch berechnet
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                            Verfügbar ab
                          </label>
                          <input
                            type="date"
                            value={row.available_from ?? ""}
                            onChange={(e) =>
                              modal.handleCoLivingRoomChange(
                                idx,
                                "available_from",
                                e.target.value
                              )
                            }
                            className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-3 py-2 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                {modal.isCoLivingType ? (
                  <div className="md:col-span-2">
                    <p className="rounded-[10px] border border-blue-500/[0.12] bg-blue-500/[0.06] px-4 py-3 text-xs text-[#7aaeff]">
                      Belegung der Einheit folgt aus Mietverhältnissen; Vorschau-KPI nutzt 0 belegte
                      Zimmer.
                    </p>
                  </div>
                ) : null}

                {!modal.isCoLivingType ? (
                  <div>
                    <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Verfügbar ab
                    </label>
                    <input
                      type="date"
                      name="availableFrom"
                      value={modal.formData.availableFrom}
                      onChange={modal.handleChange}
                      required
                      className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                    />
                  </div>
                ) : null}

                {modal.isCoLivingType ? (
                  <div className="md:col-span-2 space-y-4">
                    <div>
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Monatliche geschätzte Einnahmen
                      </label>
                      <div
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff]"
                        aria-readonly="true"
                      >
                        {formatCurrency(modal.coLivingFullOccupancyRevenue)}
                        <span className="mt-1 block text-xs font-normal text-[#64748b] dark:text-[#6b7a9a]">
                          Summe der geplanten Zimmerpreise bei Vollbelegung
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Monatliche Gesamtkosten
                      </label>
                      <div
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff]"
                        aria-readonly="true"
                      >
                        {formatCurrencyChf2(modal.derivedMonthlyTotalCosts)}
                        <span className="mt-1 block text-xs font-normal text-[#64748b] dark:text-[#6b7a9a]">
                          Monatlich (voll) + jährlich (/12) + Kautionsversicherung (/12), einmalig ausgeschlossen.
                        </span>
                        {modal.derivedOneTimeCostsTotal > 0 ? (
                          <span className="mt-1 block text-xs font-normal text-[#64748b] dark:text-[#6b7a9a]">
                            Einmalige Kosten gesamt: {formatCurrencyChf2(modal.derivedOneTimeCostsTotal)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Monatliche Gesamtkosten
                    </label>
                    <div
                      className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff]"
                      aria-readonly="true"
                    >
                      {formatCurrencyChf2(modal.derivedMonthlyTotalCosts)}
                          <span className="mt-1 block text-xs font-normal text-[#64748b] dark:text-[#6b7a9a]">
                        Monatlich (voll) + jährlich (/12) + Kautionsversicherung (/12), einmalig ausgeschlossen.
                      </span>
                      {modal.derivedOneTimeCostsTotal > 0 ? (
                        <span className="mt-1 block text-xs font-normal text-[#64748b] dark:text-[#6b7a9a]">
                          Einmalige Kosten gesamt: {formatCurrencyChf2(modal.derivedOneTimeCostsTotal)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}

                <div className="md:col-span-2 rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4">
                  <p className="mb-3 text-[13px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                    Kostenpositionen
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-[#0f172a] dark:text-[#eef2ff]">
                      <thead>
                        <tr className="border-b border-black/10 dark:border-white/[0.05] bg-slate-100 dark:bg-[#111520] text-[9px] font-bold uppercase tracking-[.8px] text-[#64748b] dark:text-[#6b7a9a]">
                          <th className="py-2 pr-4">Kostenart</th>
                          <th className="py-2 pr-4">Frequenz</th>
                          <th className="py-2 pr-4">Betrag (CHF)</th>
                          <th className="w-24 py-2 pr-4"> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {modal.modalCostRows.map((row) => (
                          <Fragment key={row.id}>
                            <tr
                              className={
                                row.cost_type === "Sonstiges"
                                  ? "align-top"
                                  : "border-b border-black/10 dark:border-white/[0.05] align-top"
                              }
                            >
                              <td className="py-2 pr-4">
                                <select
                                  value={row.cost_type}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    modal.updateModalCostRow(row.id, {
                                      cost_type: v,
                                      custom_type: v === "Sonstiges" ? row.custom_type : "",
                                    });
                                  }}
                                  disabled={modal.saving}
                                  className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none disabled:opacity-50"
                                >
                                  <option value="">— wählen —</option>
                                  {MODAL_COST_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="py-2 pr-4">
                                <select
                                  value={row.frequency || "monthly"}
                                  onChange={(e) =>
                                    modal.updateModalCostRow(row.id, { frequency: e.target.value })
                                  }
                                  disabled={modal.saving}
                                  className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none disabled:opacity-50"
                                >
                                  <option value="monthly">Monatlich</option>
                                  <option value="yearly">Jährlich</option>
                                  <option value="one_time">Einmalig</option>
                                </select>
                              </td>
                              <td className="py-2 pr-4">
                                <input
                                  type="number"
                                  value={row.amount_chf}
                                  onChange={(e) =>
                                    modal.updateModalCostRow(row.id, {
                                      amount_chf: e.target.value,
                                    })
                                  }
                                  disabled={modal.saving}
                                  className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none disabled:opacity-50"
                                />
                              </td>
                              <td className="py-2 pr-4">
                                <button
                                  type="button"
                                  disabled={modal.saving}
                                  onClick={() => modal.removeModalCostRow(row.id)}
                                  className="text-sm font-medium text-[#f87171] hover:underline disabled:opacity-50"
                                >
                                  Löschen
                                </button>
                              </td>
                            </tr>
                            {row.cost_type === "Sonstiges" ? (
                              <tr className="border-b border-black/10 dark:border-white/[0.05]">
                                <td colSpan={4} className="pb-3 pr-4 pt-0">
                                  <input
                                    type="text"
                                    value={row.custom_type}
                                    onChange={(e) =>
                                      modal.updateModalCostRow(row.id, {
                                        custom_type: e.target.value,
                                      })
                                    }
                                    disabled={modal.saving}
                                    placeholder="Bezeichnung"
                                    className="w-full max-w-md rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none disabled:opacity-50"
                                  />
                                </td>
                              </tr>
                            ) : null}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    disabled={modal.saving}
                    onClick={modal.addModalCostRow}
                    className="mt-3 rounded-[8px] border border-black/10 dark:border-white/[0.1] bg-transparent px-4 py-2 text-sm font-medium text-[#64748b] hover:bg-slate-100 dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:opacity-50"
                  >
                    + Kostenart hinzufügen
                  </button>
                </div>

                <div>
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Kautionsart Vermieter
                  </label>
                  <select
                    name="landlordDepositType"
                    value={modal.formData.landlordDepositType}
                    onChange={modal.handleChange}
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  >
                    <option value="">—</option>
                    <option value="bank">Bank</option>
                    <option value="insurance">Versicherung</option>
                    <option value="cash">Bar</option>
                    <option value="none">Keine</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Kautionsbetrag Vermieter
                  </label>
                  <input
                    type="number"
                    name="landlordDepositAmount"
                    value={modal.formData.landlordDepositAmount}
                    onChange={modal.handleChange}
                    min="0"
                    step="0.01"
                    placeholder="z. B. 5000"
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  />
                </div>

                {modal.formData.landlordDepositType === "insurance" ? (
                  <div>
                    <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Jahresprämie Vermieter
                    </label>
                    <input
                      type="number"
                      name="landlordDepositAnnualPremium"
                      value={modal.formData.landlordDepositAnnualPremium}
                      onChange={modal.handleChange}
                      min="0"
                      step="0.01"
                      placeholder="z. B. 350"
                      className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                    />
                  </div>
                ) : null}

                {/* K4b.1: the issuer, beside the premium and under the same
                    condition — both only exist for an insured security. This is
                    the ONLY place a provider is edited; /admin/kautionen shows
                    it read-only, so one value cannot be written through two
                    validations. */}
                {modal.formData.landlordDepositType === "insurance" ? (
                  <div>
                    <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                      Kautionsanbieter
                    </label>
                    <select
                      name="landlordDepositProvider"
                      value={modal.formData.landlordDepositProvider}
                      onChange={modal.handleChange}
                      className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                    >
                      <option value="">—</option>
                      <option value="swisscaution">SwissCaution</option>
                      <option value="smartcaution">SmartCaution</option>
                      <option value="firstcaution">FirstCaution</option>
                      <option value="gocaution">goCaution</option>
                      <option value="other">Andere</option>
                    </select>
                  </div>
                ) : null}

                <div className="md:col-span-2 mt-1 border-t border-black/10 dark:border-white/[0.05] pt-5">
                  <p className="mb-3 text-[13px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                    Vertrag Vermieter
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Vertragsart
                      </label>
                      <select
                        name="leaseType"
                        value={modal.formData.leaseType}
                        onChange={modal.handleChange}
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                      >
                        <option value="">—</option>
                        <option value="open_ended">Unbefristet</option>
                        <option value="fixed_term">Befristet</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Mietbeginn
                      </label>
                      <input
                        type="date"
                        name="leaseStartDate"
                        value={modal.formData.leaseStartDate}
                        onChange={modal.handleChange}
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                      />
                    </div>

                    {modal.formData.leaseType === "fixed_term" ? (
                      <div>
                        <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                          Vertragsende
                        </label>
                        <input
                          type="date"
                          name="leaseEndDate"
                          value={modal.formData.leaseEndDate}
                          onChange={modal.handleChange}
                          className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                        />
                      </div>
                    ) : null}

                    <div>
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Kündigung eingereicht am
                      </label>
                      <input
                        type="date"
                        name="noticeGivenDate"
                        value={modal.formData.noticeGivenDate}
                        onChange={modal.handleChange}
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Kündigung wirksam per
                      </label>
                      <input
                        type="date"
                        name="terminationEffectiveDate"
                        value={modal.formData.terminationEffectiveDate}
                        onChange={modal.handleChange}
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Rückgabe erfolgt am
                      </label>
                      <input
                        type="date"
                        name="returnedToLandlordDate"
                        value={modal.formData.returnedToLandlordDate}
                        onChange={modal.handleChange}
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Vertragsstatus
                      </label>
                      <div
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff]"
                        aria-readonly="true"
                      >
                        {leaseStatusLabel(computeLeaseStatusKey(modal.formData))}
                        <span className="mt-1 block text-xs font-normal text-[#64748b] dark:text-[#6b7a9a]">
                          Automatisch berechnet aus Mietbeginn / Kündigung / Rückgabe.
                        </span>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-2 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                        Notizen
                      </label>
                      <textarea
                        name="leaseNotes"
                        value={modal.formData.leaseNotes}
                        onChange={modal.handleChange}
                        rows={3}
                        className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none resize-y min-h-[5rem]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {!modal.isCoLivingType ? (
                <div className="mt-6">
                  <label className="mb-1 block text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                    Geplante Miete / Monat (CHF)
                  </label>
                  <input
                    type="number"
                    name="tenantPriceMonthly"
                    value={modal.formData.tenantPriceMonthly}
                    onChange={modal.handleChange}
                    min="0"
                    step="1"
                    placeholder="z. B. 1500"
                    className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-4 py-3 text-[#0f172a] dark:text-[#eef2ff] outline-none"
                  />
                  <p className="mt-1 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                    Planungswert, nicht die tatsächliche Miete. Die tatsächliche Miete wird beim Mietverhältnis erfasst.
                  </p>
                </div>
              ) : null}

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {!modal.isCoLivingType ? (
                  <>
                    <div className="rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4">
                      <p className="text-[11px] text-[#64748b] dark:text-[#6b7a9a]">Geplante Miete − Kosten (Stammdaten)</p>
                      <p className="mt-1 text-[24px] font-bold text-[#4ade80]">
                        {formatCurrency(modal.currentApartmentProfit)}
                      </p>
                    </div>

                    <div className="rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4">
                      <p className="text-[11px] text-[#64748b] dark:text-[#6b7a9a]">Zimmer</p>
                      <p className="mt-1 text-[24px] font-bold text-[#0f172a] dark:text-[#eef2ff]">
                        {modal.formData.rooms || 0}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4">
                      <p className="text-[11px] text-[#64748b] dark:text-[#6b7a9a]">Einnahmen (Mieter-Äquivalent)</p>
                      <p className="mt-1 text-[24px] font-bold text-[#4ade80]">
                        {formatCurrency(modal.currentCoLivingRevenue)}
                      </p>
                      <p className="mt-1 text-xs text-[#64748b] dark:text-[#6b7a9a]">
                        Aus TenancyRevenue-Äquivalent; kein Backend-KPI-Monat.
                      </p>
                    </div>

                    <div className="rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4">
                      <p className="text-[11px] text-[#64748b] dark:text-[#6b7a9a]">Potenzial − Äquivalent</p>
                      <p className="mt-1 text-[24px] font-bold text-[#fb923c]">
                        {formatCurrency(modal.currentCoLivingVacancy)}
                      </p>
                    </div>

                    <div className="rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4">
                      <p className="text-[11px] text-[#64748b] dark:text-[#6b7a9a]">Deckungsbeitrag (Frontend)</p>
                      <p className="mt-1 text-[24px] font-bold text-[#4ade80]">
                        {formatCurrency(modal.currentCoLivingProfit)}
                      </p>
                    </div>

                    <div className="rounded-[10px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] p-4">
                      <p className="text-[11px] text-[#64748b] dark:text-[#6b7a9a]">Freie Zimmer</p>
                      <p className="mt-1 text-[24px] font-bold text-[#0f172a] dark:text-[#eef2ff]">
                        {modal.currentFreeRooms}
                      </p>
                    </div>
                  </>
                )}
              </div>

              {!modal.formLeaseStarted && (
                <div className="mt-4 rounded-[10px] border border-amber-500/[0.15] bg-amber-500/[0.06] p-4">
                  <p className="text-sm text-[#fbbf24]">
                    Hinweis: Der Mietbeginn im Vertrag Vermieter liegt in der Zukunft.
                    Deshalb werden die aktuellen KPI noch ohne laufende Monatskosten gerechnet.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={modal.handleCloseModal}
                  className="rounded-[8px] border border-black/10 dark:border-white/[0.1] bg-transparent px-5 py-3 font-semibold text-[#64748b] hover:bg-slate-100 dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
                >
                  Abbrechen
                </button>

                <button
                  type="submit"
                  disabled={modal.saving}
                  className="rounded-[8px] border-none bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-5 py-3 font-semibold text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {modal.saving ? "Speichern …" : modal.editingId ? "Änderungen speichern" : "Unit speichern"}
                </button>
              </div>
            </form>
          </div>
        </div>
  );
}
